import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import DataTable from "react-data-table-component";
import BreadCrumb from "../../../Components/Common/BreadCrumb";

// Mock Data
const cardDetail = {
  card_uid: "CARD123456",
  user_id: "U001",
  user_type: "Student",
  user_name: "John Doe",
  status: "Active",
  issued_date: "2026-01-01",
  expiry_date: "2028-01-01",
  issued_by: "Admin",
};

const wallet = {
  balance: 2500,
  daily_limit: 1000,
  daily_spent_today: 300,
  tx_limit: 500,
  total_recharged: 5000,
  total_spent: 2500,
  last_reset_date: "2026-03-01",
  status: "Active",
};

const transactions = [
  {
    id: 1,
    amount: 200,
    type: "Debit",
    reference_id: "TX1001",
    status: "Completed",
    balance_before: 2700,
    balance_after: 2500,
    kiosk_id: "K01",
    created_at: "2026-04-01 10:30:00",
  },
  {
    id: 2,
    amount: 500,
    type: "Credit",
    reference_id: "TX1002",
    status: "Completed",
    balance_before: 2200,
    balance_after: 2700,
    kiosk_id: "K01",
    created_at: "2026-03-30 14:20:00",
  },
];

const issuanceLogs = [
  { id: 1, issued_by: "Admin", issued_at: "2026-01-01 09:00:00" },
];

const rechargeLogs = [
  {
    id: 1,
    amount: 500,
    staff_id: "S001",
    previous_balance: 2000,
    new_balance: 2500,
    payment_method: "Cash",
    remarks: "Monthly recharge",
    created_at: "2026-03-31 12:00:00",
  },
];

const CardPaymentDetail = () => {
  const [activeTab, setActiveTab] = useState("1");

  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  // Columns for react-data-table-component
  const transactionColumns = [
    { name: "ID", selector: (row) => row.id, sortable: true },
    { name: "Type", selector: (row) => row.type, sortable: true },
    {
      name: "Amount",
      selector: (row) => `${row.amount.toFixed(2)}`,
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => (
        <Badge
          color={
            row.status === "Completed"
              ? "success"
              : row.status === "Pending"
              ? "warning"
              : "danger"
          }
        >
          {row.status}
        </Badge>
      ),
    },
    {
      name: "Balance Before",
      selector: (row) => `${row.balance_before.toFixed(2)}`,
      sortable: true,
    },
    {
      name: "Balance After",
      selector: (row) => `${row.balance_after.toFixed(2)}`,
      sortable: true,
    },
    { name: "Kiosk", selector: (row) => row.kiosk_id, sortable: true },
    { name: "Created At", selector: (row) => row.created_at, sortable: true },
  ];

  const issuanceColumns = [
    { name: "ID", selector: (row) => row.id, sortable: true },
    { name: "Issued By", selector: (row) => row.issued_by, sortable: true },
    { name: "Issued At", selector: (row) => row.issued_at, sortable: true },
  ];

  const rechargeColumns = [
    { name: "ID", selector: (row) => row.id, sortable: true },
    {
      name: "Amount",
      selector: (row) => `${row.amount.toFixed(2)}`,
      sortable: true,
    },
    { name: "Staff ID", selector: (row) => row.staff_id, sortable: true },
    {
      name: "Previous Balance",
      selector: (row) => `${row.previous_balance.toFixed(2)}`,
      sortable: true,
    },
    {
      name: "New Balance",
      selector: (row) => `${row.new_balance.toFixed(2)}`,
      sortable: true,
    },
    {
      name: "Payment Method",
      selector: (row) => row.payment_method,
      sortable: true,
    },
    { name: "Remarks", selector: (row) => row.remarks, sortable: true },
    { name: "Created At", selector: (row) => row.created_at, sortable: true },
  ];
  const breadcrumbItems = [
    { title: "Back To List", onClick: () => navigate(-1) },
  ];
  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Card Payment"
          pageTitle="Payments"
          breadcrumbItems={breadcrumbItems}
        />
        <Row>
          {/* Card Info */}
          <Col md="6">
            <Card className="mb-3">
              <CardHeader>Card Info</CardHeader>
              <CardBody>
                <p>
                  <strong>Card UID:</strong> {cardDetail.card_uid}
                </p>
                <p>
                  <strong>User:</strong> {cardDetail.user_name} (
                  {cardDetail.user_type})
                </p>
                <p>
                  <strong>Status:</strong>{" "}
                  <Badge
                    color={
                      cardDetail.status === "Active" ? "success" : "danger"
                    }
                  >
                    {cardDetail.status}
                  </Badge>
                </p>
                <p>
                  <strong>Issued Date:</strong> {cardDetail.issued_date}
                </p>
                <p>
                  <strong>Expiry Date:</strong> {cardDetail.expiry_date}
                </p>
                <p>
                  <strong>Issued By:</strong> {cardDetail.issued_by}
                </p>
              </CardBody>
            </Card>
          </Col>

          {/* Wallet Info */}
          <Col md="6">
            <Card className="mb-3">
              <CardHeader>Wallet Info</CardHeader>
              <CardBody>
                <p>
                  <strong>Balance:</strong> ${wallet.balance.toFixed(2)}
                </p>
                <p>
                  <strong>Daily Limit:</strong> ${wallet.daily_limit.toFixed(2)}
                </p>
                <p>
                  <strong>Spent Today:</strong> $
                  {wallet.daily_spent_today.toFixed(2)}
                </p>
                <p>
                  <strong>Transaction Limit:</strong> $
                  {wallet.tx_limit.toFixed(2)}
                </p>
                <p>
                  <strong>Total Recharged:</strong> $
                  {wallet.total_recharged.toFixed(2)}
                </p>
                <p>
                  <strong>Total Spent:</strong> ${wallet.total_spent.toFixed(2)}
                </p>
                <p>
                  <strong>Status:</strong>{" "}
                  <Badge
                    color={wallet.status === "Active" ? "success" : "danger"}
                  >
                    {wallet.status}
                  </Badge>
                </p>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Tabs */}
        <Card>
          <CardHeader>
            <Nav tabs>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "1" })}
                  onClick={() => toggle("1")}
                >
                  Recent Transactions
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "2" })}
                  onClick={() => toggle("2")}
                >
                  Issuance Logs
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "3" })}
                  onClick={() => toggle("3")}
                >
                  Recharge Logs
                </NavLink>
              </NavItem>
            </Nav>
          </CardHeader>
          <CardBody>
            <TabContent activeTab={activeTab}>
              <TabPane tabId="1">
                <DataTable
                  columns={transactionColumns}
                  data={transactions}
                  pagination
                  responsive
                  highlightOnHover
                />
              </TabPane>
              <TabPane tabId="2">
                <DataTable
                  columns={issuanceColumns}
                  data={issuanceLogs}
                  pagination
                  responsive
                  highlightOnHover
                />
              </TabPane>
              <TabPane tabId="3">
                <DataTable
                  columns={rechargeColumns}
                  data={rechargeLogs}
                  pagination
                  responsive
                  highlightOnHover
                />
              </TabPane>
            </TabContent>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default CardPaymentDetail;
